<template>
  <div class="membership-card-builder">
    <div class="row">
      <div class="col-md-6">
        <h5 class="mb-3">{{ $t('customize_membership_card') }}</h5>
        
        <!-- Card Settings -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">{{ $t('card_design') }}</h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('background_color') }}</label>
              <input type="color" v-model="cardSettings.backgroundColor" class="form-control form-control-color w-100" />
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('text_color') }}</label>
              <input type="color" v-model="cardSettings.textColor" class="form-control form-control-color w-100" />
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('border_radius') }}</label>
              <input type="range" v-model="cardSettings.borderRadius" min="0" max="30" class="form-range" />
              <small class="text-muted">{{ cardSettings.borderRadius }}px</small>
            </div>
          </div>
        </div>

        <!-- Text Settings -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">{{ $t('text_settings') }}</h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('tagline') }}</label>
              <input type="text" v-model="cardSettings.tagline" class="form-control" :placeholder="$t('premium_membership_card')" />
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('show_logo') }}</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="cardSettings.showLogo" />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('show_qr_code') }}</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="cardSettings.showQRCode" />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('show_contact_info') }}</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="cardSettings.showContactInfo" />
              </div>
            </div>
          </div>
        </div>

        <!-- Layout Settings -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">{{ $t('layout') }}</h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('qr_code_position') }}</label>
              <select v-model="cardSettings.qrPosition" class="form-select">
                <option value="right">{{ $t('right') }}</option>
                <option value="left">{{ $t('left') }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex gap-2 mb-3">
          <button @click="saveTemplate" class="btn btn-primary flex-fill">
            <AppIcon name="bi-save" class="bi bi-save" /> {{ $t('save_template') }}
          </button>
          <button @click="resetToDefaults" class="btn btn-secondary flex-fill">
            <AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" /> {{ $t('reset') }}
          </button>
          <button v-if="template_id" @click="deleteTemplate" class="btn btn-danger">
            <AppIcon name="bi-trash" class="bi bi-trash" />
          </button>
        </div>
      </div>

      <!-- Preview Column -->
      <div class="col-md-6">
        <h5 class="mb-3">{{ $t('preview') }}</h5>
        <div class="preview-container">
          <div class="membership-card-preview" :style="cardPreviewStyle">
            <div class="card-front">
              <!-- Header with Logo -->
              <div class="card-header-inner" :style="{ borderBottomColor: cardSettings.textColor + '33' }">
                <img v-if="cardSettings.showLogo && business.logo" :src="business.logo" alt="Business Logo" class="business-logo" />
                <div class="company-info">
                  <h3 class="company-name" :style="{ color: cardSettings.textColor }">{{ business.name }}</h3>
                  <p class="company-tagline" :style="{ color: cardSettings.textColor }">{{ cardSettings.tagline || 'Premium Membership Card' }}</p>
                </div>
              </div>

              <!-- Main Content -->
              <div class="card-body-inner">
                <div class="client-section" :class="{'flex-row-reverse': cardSettings.qrPosition === 'left'}">
                  <div class="client-info">
                    <h2 class="client-name" :style="{ color: cardSettings.textColor }">{{ $t('sample_client_name') }}</h2>
                    <div class="membership-info">
                      <div class="info-item">
                        <span class="info-label" :style="{ color: cardSettings.textColor, opacity: 0.7 }">{{ $t('member_since') }}</span>
                        <span class="info-value" :style="{ color: cardSettings.textColor }">{{ formatDate(new Date()) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label" :style="{ color: cardSettings.textColor, opacity: 0.7 }">Member ID</span>
                        <span class="info-value" :style="{ color: cardSettings.textColor }">#12345</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="qr-section" v-if="cardSettings.showQRCode">
                    <div class="qr-container">
                      <div class="qr-placeholder">
                        <AppIcon name="bi-qr-code" class="bi bi-qr-code" style="font-size: 3rem;" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="card-footer-inner" v-if="cardSettings.showContactInfo" :style="{ borderTopColor: cardSettings.textColor + '33' }">
                <div class="contact-info">
                  <span class="contact-item" v-if="business.email" :style="{ color: cardSettings.textColor }">
                    <AppIcon name="bi-envelope" class="bi bi-envelope" />
                    {{ business.email }}
                  </span>
                  <span class="contact-item" v-if="business.phone" :style="{ color: cardSettings.textColor }">
                    <AppIcon name="bi-phone" class="bi bi-phone" />
                    {{ business.phone }}
                  </span>
                  <span class="contact-item" v-if="business.website" :style="{ color: cardSettings.textColor }">
                    <AppIcon name="bi-globe" class="bi bi-globe" />
                    {{ business.website }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';

export default {
  name: 'MembershipCardTemplateBuilder',
  props: {
    template_id: {
      type: Number,
      default: null
    },
    template: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      cardSettings: {
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        borderRadius: 15,
        tagline: 'Premium Member',
        showLogo: true,
        showQRCode: true,
        showContactInfo: true,
        qrPosition: 'right'
      }
    };
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS;
    },
    cardPreviewStyle() {
      return {
        background: this.cardSettings.backgroundColor,
        color: this.cardSettings.textColor,
        borderRadius: `${this.cardSettings.borderRadius}px`
      };
    },
  },
  methods: {
    formatDate(date) {
      return helper.formatDate(date);
    },
    async saveTemplate() {
      try {
        const templateData = {
          type: 'membership_card',
          name: 'Membership Card Template',
          data: JSON.stringify(this.cardSettings)
        };

        if (this.template_id) {
          await helper.customPatch(
            `${process.env.VUE_APP_API}/templates/${this.template_id}`,
            templateData,
            { showToast: true }
          );
        } else {
          await helper.customPost(
            `${process.env.VUE_APP_API}/templates`,
            templateData,
            { showToast: true }
          );
        }

        this.$emit('templateSaved');
      } catch (error) {
        alert('Error saving template:', error);
      }
    },
    async deleteTemplate() {
      if (!this.template_id) return;
      
      if (confirm(this.$t('are_you_sure_delete_template'))) {
        try {
          await helper.customDelete(
            `${process.env.VUE_APP_API}/templates/${this.template_id}`,
            { showToast: true }
          );
          this.$emit('templateDeleted');
        } catch (error) {
          alert('Error deleting template:', error);
        }
      }
    },
    resetToDefaults() {
      if (confirm(this.$t('are_you_sure_reset_to_defaults'))) {
        this.cardSettings = {
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          borderRadius: 15,
          tagline: 'Premium Member',
          showLogo: true,
          showQRCode: true,
          showContactInfo: true,
          qrPosition: 'right'
        };
      }
    }
  },
  mounted() {
    if (this.template) {
      try {
        const settings = typeof this.template === 'string' 
          ? JSON.parse(this.template) 
          : this.template;
        this.cardSettings = { ...this.cardSettings, ...settings };
      } catch (error) {
        alert('Error parsing template:', error);
      }
    }
  }
};
</script>

<style scoped>
.membership-card-builder {
  padding: 1rem;
}

.preview-container {
  position: sticky;
  top: 20px;
}

.membership-card-preview {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  margin-bottom: 2rem;
}

.card-front {
  padding: 2rem;
}

.card-header-inner {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid;
}

.business-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
  background: white;
  border-radius: 12px;
  padding: 0.5rem;
}

.company-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

.company-tagline {
  margin: 0;
  opacity: 0.9;
  font-size: 0.75rem;
}

.card-body-inner {
  padding: 1rem 0;
}

.client-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.client-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.membership-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 600;
}

.qr-section {
  flex-shrink: 0;
}

.qr-container {
  background: white;
  padding: 0.75rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  min-width: 140px;
}

.qr-placeholder {
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-footer-inner {
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid;
}

.contact-info {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  font-size: 0.75rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-item i {
  font-size: 0.875rem;
}
</style>
